import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import Segmented                 from '@widesk-ui/Segmented';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import AreaPlaceSection          from '@/TokamapEditor/ux/AreaSidebar/AreaPlaceSection';
import AreaUrlSection            from '@/TokamapEditor/ux/AreaSidebar/AreaUrlSection';
import AreaMapSection            from '@/TokamapEditor/ux/AreaSidebar/AreaMapSection';
import Choice                    from '@/TokamapEditor/ux/AreaSidebar/Choice';
import Field                     from '@/TokamapEditor/ux/AreaSidebar/Field';
import HelpTip                   from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import SectionTitle              from '@/TokamapEditor/ux/AreaSidebar/SectionTitle';
import SettingsButton            from '@/TokamapEditor/ux/AreaSidebar/SettingsPopover';
import { commonValue }           from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import { ExtraAction }           from '@models/map/FeatureModel';
import { observer }              from 'mobx-react-lite';
import useDesignTokens           from '@/hooks/useDesignTokens';
import FunctionalityGate         from '@/components/Functionality';
import unlinkPlaceContent        from '@/TokamapEditor/helpers/unlinkPlaceContent';
import asSingleHistoryEntry      from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import shapePlaceId              from '@/TokamapEditor/helpers/shapePlaceId';
import { placeLabel }            from '@/TokamapEditor/helpers/placeLinkedContent';
import PlaceModel                from '@models/map/PlaceModel';
import ToolPreview               from '@/TokamapEditor/ux/ToolPreview';
import { DemoMark }              from '@/TokamapEditor/ux/ToolPreview';
import { shapePassage }          from '@tokamap/core';
import { type Shape }            from '@tokamap/core';
import { type ShapePassage }     from '@tokamap/core';
import { SelectOutlined }        from '@ant-design/icons';
import { ArrowRightOutlined }    from '@ant-design/icons';
import { StopOutlined }          from '@ant-design/icons';
import { LoginOutlined }         from '@ant-design/icons';
import { NodeIndexOutlined }     from '@ant-design/icons';

// Valeurs de `Shape.pathPriority` — voir CLAUDE.md "Itinéraire / pathfinding" (`@tokamap/core`) :
// numérique côté lib (`pathPriorityWeight` décroît en continu avec cette valeur), relibellé ici en
// 3 paliers "attractivité" (Faible/Moyenne/Forte) pour rester compréhensible sans exposer l'échelle
// numérique brute à l'utilisateur.
const PATH_PRIORITY_OPTIONS: { label: string; value: NonNullable<Shape['pathPriority']> | ''; }[] = [
	{ label: 'Aucune', value: '' },
	{ label: 'Faible', value: 1 },
	{ label: 'Moyenne', value: 3 },
	{ label: 'Forte', value: 6 },
];

const PASSAGES: { value: ShapePassage; label: string; hint: string; icon: React.ReactNode; }[] = [
	{ value: 'free', label: 'Libre', hint: 'Le visiteur peut traverser la forme : allée, place, parvis.', icon: <ArrowRightOutlined /> },
	{ value: 'blocked', label: 'Bloqué', hint: 'L\'itinéraire contourne la forme : stand, mur, bassin.', icon: <StopOutlined /> },
	{
		value: 'walls',
		label: 'Par les ouvertures',
		hint: 'On entre et on sort par ses ouvertures (outil Ouverture, touche O) : hall, bâtiment.',
		icon: <LoginOutlined />,
	},
];

const AreaActionSection = observer(() => {
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	return (
		<View gap="lg" lineHeight={1.5}>
			<View gap="sm">
				<SectionTitle>Au clic du visiteur</SectionTitle>
				<ClickButton />
			</View>

			<View gap="sm">
				<SectionTitle>Dans les itinéraires</SectionTitle>
				<PassageButton />
			</View>
		</View>
	);
});

const ClickButton = observer(() => {
	const tokens = useDesignTokens();
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	const commonAction = commonValue(shapes, s => s.action);
	// `commonAction === undefined` peut aussi bien dire "action mixte" que "aucune action définie
	// nulle part" (cas courant, single ou multi) — seul `Set` distingue vraiment les deux.
	const isMixed = shapes.length > 1 && new Set(shapes.map(s => s.action)).size > 1;

	const single = !currentEditorStore.isMultiSelection ? shapes[0] : undefined;
	const linkedShape = single && shapePlaceId(single) ? single : undefined;
	const placeName = PlaceModel.localize('default', 'l');

	const url = commonValue(shapes, s => s.urlAction?.url);
	const targetMapId = commonValue(shapes, s => s.targetDocumentId);
	const targetMap = targetMapId ? currentProjectEditorStore.get.mapCollection.models.find(map => map.id === targetMapId) : undefined;

	const summary = (() => {
		if (isMixed) return 'Plusieurs valeurs';

		switch (commonAction) {
			case 'open_place':
				return single
					? `Fiche du ${placeName} · ${(linkedShape && placeLabel(linkedShape)) || 'non associée'}`
					: `Afficher la fiche du ${placeName}`;
			case 'open_url':
				return `Page web · ${url ? url.replace(/^https?:\/\//, '') : 'aucune adresse'}`;
			case 'open_map':
				return `Autre carte · ${targetMap?.label || 'aucune choisie'}`;
			default:
				return 'Aucune action';
		}
	})();

	return (
		<SettingsButton
			width={300}
			label="Au clic"
			summary={summary}
			mixed={isMixed}
			preview={<SelectOutlined style={{ fontSize: 20, color: tokens.text.secondary, flexShrink: 0 }} />}
		>
			<ClickSettings />
		</SettingsButton>
	);
});

const ClickSettings = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const tokens = useDesignTokens();

	if (!shapes.length) return null;

	const commonAction = commonValue(shapes, s => s.action);
	const isMixed = shapes.length > 1 && new Set(shapes.map(s => s.action)).size > 1;

	const withMapNestingGate = (choice: React.ReactNode) => (!isMixed && commonAction === 'open_map')
		? choice
		: <FunctionalityGate reference="map_nesting" fullWidth>{choice}</FunctionalityGate>;

	// Seul point d'entrée qui change `Shape.action` (`AreaPlaceSection`/`AreaUrlSection` ne font que
	// compléter la configuration d'une action déjà choisie ici) — c'est donc ici, et ici seulement,
	// qu'on vide la configuration de l'action désormais inactive. Sans ça, un consommateur qui lit
	// `shape.entity`/`shape.urlAction` sans vérifier `shape.action` (ex. `PreviewModal.tsx`) verrait
	// une configuration périmée comme toujours active après un changement d'action.
	const setAction = (value?: ExtraAction) => asSingleHistoryEntry(async () => {
		const ids = shapes.map(s => s.id);

		editor.setShapeAction(ids, value);

		if (value !== 'open_place') {
			const shapesWithEntity = shapes.filter(s => s.entity !== undefined);

			if (shapesWithEntity.length) {
				await unlinkPlaceContent(shapesWithEntity);
				editor.setShapeEntity(shapesWithEntity.map(s => s.id), undefined);
			}
		}

		if (value !== 'open_url') {
			const idsWithUrlAction = shapes.filter(s => s.urlAction !== undefined).map(s => s.id);

			if (idsWithUrlAction.length) editor.setShapeUrlAction(idsWithUrlAction, undefined);
		}

		if (value !== 'open_map') {
			const idsWithTargetMap = shapes.filter(s => s.targetDocumentId !== undefined).map(s => s.id);

			if (idsWithTargetMap.length) editor.setShapeTargetDocumentId(idsWithTargetMap, undefined);
		}
	});

	const single = !currentEditorStore.isMultiSelection ? shapes[0] : undefined;
	const linkedShape = single && shapePlaceId(single) ? single : undefined;
	const placeName = PlaceModel.localize('default', 'l');

	return (
		<>
			{isMixed && (
				<View size={12} color={tokens.text.muted}>
					Valeurs différentes selon les formes sélectionnées.
				</View>
			)}

			<View gap="xs">
				{/* La seule des trois à porter une explication. « Point d'intérêt » est le nom de l'entité
				    dans tout le produit, mais cette liste est souvent l'endroit où on le rencontre EN
				    PREMIER : sur un projet neuf la liste des points d'intérêt est vide, et la phrase
				    d'`AreaPlaceSection` qui l'explique n'apparaît qu'une fois l'option choisie — après la
				    décision qu'elle devait éclairer. On ne définit pas le terme, on dit ce que verra le
				    visiteur : ça glose le mot et donne du même coup la conséquence qui manque au libellé.
				    Le contenu décrit le vrai panneau (voir `PlacePanelPreview`). */}
				<ToolPreview name="placePanel" label={`Afficher la fiche du ${placeName}`} placement="right">
					<Choice
						selected={!isMixed && commonAction === 'open_place'}
						onClick={() => setAction('open_place')}
						hint="Elle s'ouvre par-dessus le plan, avec son image, son nom et ses informations."
					>
						<span>Afficher la fiche du {placeName}<DemoMark /></span>
					</Choice>
				</ToolPreview>

				<Choice selected={!isMixed && commonAction === 'open_url'} onClick={() => setAction('open_url')}>
					Ouvrir une page web
				</Choice>

				{/* Le libellé calque celui du point d'intérêt (« Afficher… ») : dans les deux cas le visiteur
				    reste sur le plan, rien ne s'ouvre à côté. L'explication dit d'abord ce qui se passe à
				    l'écran, puis à quoi ça sert — « une autre carte » seul ne le laisse pas deviner, alors
				    qu'un escalier ou une porte se reconnaissent tout de suite dans son propre projet. */}
				{withMapNestingGate(
					<Choice
						selected={!isMixed && commonAction === 'open_map'}
						onClick={() => setAction('open_map')}
						hint="Le plan est remplacé par la carte choisie : un escalier vers un autre étage, une porte vers un autre bâtiment…"
					>
						Afficher une autre carte
					</Choice>,
				)}

				<Choice selected={!isMixed && !commonAction} onClick={() => setAction(undefined)}>
					Aucune action
				</Choice>
			</View>

			{/* Changer d'action défait la liaison (voir `setAction`) : on le dit avant le clic, plutôt que
			    de laisser la fiche disparaître sans prévenir. */}
			{!!linkedShape && commonAction === 'open_place' && (
				<View size={12} color={tokens.text.muted}>
					Choisir une autre action dissocie la forme de « {placeLabel(linkedShape) || `ce ${placeName}`} » et
					retire son nom et son image de la forme.
				</View>
			)}

			{!isMixed && commonAction === 'open_place' && (
				currentEditorStore.isMultiSelection
					? <AreaPlaceSection />
					: !shapes[0]?.entity && (
						<View size={13} color={tokens.text.muted}>
							Associez d'abord la forme à un {placeName}, en haut du panneau.
						</View>
					)
			)}
			{!isMixed && commonAction === 'open_url' && <AreaUrlSection />}
			{!isMixed && commonAction === 'open_map' && <AreaMapSection />}
		</>
	);
});

const PassageButton = observer(() => {
	const tokens = useDesignTokens();
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	const passage = commonValue(shapes, shapePassage);
	const mixed = passage === undefined;
	const blocksPassage = mixed ? undefined : passage !== 'free';
	// Une ligne, un cercle ou une image n'ont pas de murs à eux : seul un polygone peut en avoir.
	const canHaveWalls = shapes.every(s => !s.shapeKind || s.shapeKind === 'polygon' || s.shapeKind === 'rectangle' || s.shapeKind === 'circle');

	// `commonValue` ne distingue pas "toutes les formes ont `pathPriority` absent" de "mélange" (les
	// deux renvoient `undefined`) — vérification dédiée via un `Set` des valeurs réellement présentes.
	const pathPriorityMixed = new Set(shapes.map(s => s.pathPriority)).size > 1;
	const pathPriority = shapes[0]!.pathPriority ?? '';
	const priorityLabel = PATH_PRIORITY_OPTIONS.find(option => option.value === pathPriority)?.label ?? `${pathPriority}`;

	const setPathPriority = (value: unknown) => {
		editor.setShapePathPriority(shapes.map(s => s.id), (value === '' || value === null || value === undefined ? undefined : value) as Shape['pathPriority']);
	};

	const current = PASSAGES.find(option => option.value === passage);
	const summary = [
		current?.label ?? 'Plusieurs valeurs',
		pathPriorityMixed ? 'attractivité variable' : `attractivité ${priorityLabel.toLowerCase()}`,
	].join(' · ');

	return (
		<SettingsButton
			width={340}
			label="Passage"
			summary={summary}
			mixed={mixed || pathPriorityMixed}
			preview={(
				<span style={{ fontSize: 20, color: tokens.text.secondary, flexShrink: 0, display: 'flex' }}>
					{current?.icon ?? <NodeIndexOutlined />}
				</span>
			)}
		>
			<View gap="xs">
				{PASSAGES.map(option => (
					<Choice
						key={option.value}
						selected={passage === option.value}
						disabled={option.value === 'walls' && !canHaveWalls}
						hint={option.hint}
						onClick={() => editor.setShapePassage(shapes.map(s => s.id), option.value)}
					>
						{option.label}
					</Choice>
				))}
			</View>

			<Field
				label={(
					<>
						Attractivité
						<HelpTip>
							{mixed ? (
								'Incite les itinéraires piétons à passer par cette forme, ou à la longer si elle bloque le passage (mélangé sur la sélection actuelle) — plus l\'attractivité est forte, plus le détour accepté pour ça est grand.'
							) : blocksPassage ? (
								'Cette forme reste infranchissable, mais les itinéraires piétons la longeront plutôt que de s\'en éloigner (plus l\'attractivité est forte, plus le détour accepté pour ça est grand).'
							) : (
								'Les itinéraires piétons seront incités à passer par cette forme (plus l\'attractivité est forte, plus le détour accepté pour ça est grand).'
							)}
						</HelpTip>
					</>
				)}
			>
				<Segmented
					block
					value={pathPriorityMixed ? undefined : pathPriority}
					onChange={setPathPriority}
					options={PATH_PRIORITY_OPTIONS}
				/>
			</Field>
		</SettingsButton>
	);
});

export default AreaActionSection;
